import React, { useState } from 'react';
import { Box, Typography, Grid, Paper, Button, Chip, Stack, Container, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const COLOR = '#ff6eb4';

// ── Data ─────────────────────────────────────────────────────────────────────
// Add your resources here. Each item links out to an external URL.

const resources = [
  // ── Freebies ────────────────────────────────────────────────────────────────
  {
    category: 'Freebies',
    emoji: '🎁',
    title: 'Panko Cloudrig',
    desc: 'Pankos Cloudrig.',
    url: 'https://patreon.com/panekopanko',
    tags: ['Free'],
    color: '#ff6eb4',
  },

  // ── Tools ───────────────────────────────────────────────────────────────────
  {
    category: 'Tools',
    emoji: '🛠️',
    title: 'copyparty',
    desc: 'pankos copyparty file server - with files for the community to upload!',
    url: 'https://copyparty.panekopanko.se/',
    tags: ['Tool'],
    color: '#c97cff',
  },

    {
    category: 'Tools',
    emoji: '🛠️',
    title: 'file host',
    desc: 'temporary file host, upload a file and it disappears after 30 mins!',
    url: 'https://file.panekopanko.se/',
    tags: ['Tool'],
    color: '#c97cff',
  },
  
  // ── CTF & Hacking ───────────────────────────────────────────────────────────
  {
    category: 'CTF & Hacking',
    emoji: '🔐',
    title: 'impacket command generator',
    desc: 'impacket command generator, for fixing with impacket commands with similar repeated variables',
    url: 'https://impacket.panekopanko.se/',
    tags: ['CTF'],
    color: '#b44fff',
  },

    {
    category: 'CTF & Hacking',
    emoji: '🔐',
    title: 'exfiltration generator',
    desc: 'file exfiltration generator,.',
    url: 'https://exfiltrate.panekopanko.se/',
    tags: ['CTF'],
    color: '#b44fff',
  },
  // ── VTubing ─────────────────────────────────────────────────────────────────
  {
    category: 'VTubing',
    emoji: '🎙️',
    title: 'vtubermodel.wiki',
    desc: 'all you need to buy, build & stream as a vtuber!',
    url: 'https://vtubermodel.wiki',
    tags: ['VTubing'],
    color: '#ff9fd0',
  },
];

const categories = ['All', ...Array.from(new Set(resources.map((r) => r.category)))];

// ── Component ─────────────────────────────────────────────────────────────────

export default function ResourcesPage() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? resources
    : resources.filter((r) => r.category === activeCategory);

  return (
    <Box sx={{ background: '#0d0a12', minHeight: '100vh', pt: 10, pb: 16 }}>
      {/* Hero */}
      <Box sx={{
        py: { xs: 8, md: 10 },
        px: { xs: 3, md: 8 },
        background: `radial-gradient(ellipse 70% 50% at 60% 0%, ${COLOR}12 0%, transparent 70%), #0d0a12`,
        borderBottom: '1px solid rgba(255,110,180,0.1)',
      }}>
        <Container maxWidth="lg">
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowBackIcon />}
            sx={{ color: 'text.secondary', mb: 4, '&:hover': { color: 'primary.main' } }}
          >
            Home
          </Button>
          <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 6, fontSize: '0.75rem', display: 'block' }}>
            RESOURCES
          </Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', md: '4rem' }, lineHeight: 1.1, mt: 1, mb: 2 }}>
            Tools & Freebies
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '1rem', lineHeight: 1.8, maxWidth: 520 }}>
            A collection of tools, freebies, and resources I use or recommend — organised by category.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 10 } }}>
        {/* Category filter */}
        <Box sx={{ mb: 6, overflowX: 'auto' }}>
          <ToggleButtonGroup
            value={activeCategory}
            exclusive
            onChange={(_, val) => { if (val) setActiveCategory(val); }}
            sx={{
              gap: 1,
              flexWrap: 'wrap',
              '& .MuiToggleButton-root': {
                border: `1px solid ${COLOR}33 !important`,
                borderRadius: '50px !important',
                color: 'text.secondary',
                px: 2.5, py: 0.8,
                fontSize: '0.85rem',
                fontWeight: 600,
                textTransform: 'none',
                transition: 'all 0.2s',
                '&.Mui-selected': {
                  background: `${COLOR}18`,
                  borderColor: `${COLOR}88 !important`,
                  color: COLOR,
                },
                '&:hover': {
                  borderColor: `${COLOR}66 !important`,
                  color: COLOR,
                },
              },
            }}
          >
            {categories.map((cat) => (
              <ToggleButton key={cat} value={cat}>{cat}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Resource cards */}
        <Grid container spacing={3}>
          {filtered.map((item, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
              <Paper
                component="a"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  p: 3, height: '100%',
                  display: 'flex', flexDirection: 'column',
                  background: 'rgba(22,15,32,0.9)',
                  border: `1px solid ${item.color}22`,
                  textDecoration: 'none',
                  transition: 'all 0.25s',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: `${item.color}66`,
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 40px ${item.color}18`,
                  },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
                  <Typography sx={{
                    fontSize: '2rem', lineHeight: 1,
                    filter: `drop-shadow(0 0 12px ${item.color}88)`,
                    flexShrink: 0,
                  }}>
                    {item.emoji}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        {item.title}
                      </Typography>
                      <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                    </Stack>
                    <Chip
                      label={item.category}
                      size="small"
                      sx={{
                        mt: 0.5,
                        height: 18, fontSize: '0.65rem', fontWeight: 600,
                        background: `${item.color}15`,
                        border: `1px solid ${item.color}33`,
                        color: item.color,
                      }}
                    />
                  </Box>
                </Stack>

                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, flex: 1, mb: 2 }}>
                  {item.desc}
                </Typography>

                <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.8 }}>
                  {item.tags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" sx={{
                      fontSize: '0.63rem',
                      background: `${item.color}10`,
                      border: `1px solid ${item.color}25`,
                      color: item.color,
                    }} />
                  ))}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
